import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SpinnerWheel } from "./index";
import ScratchCard from "./ScratchCard";
import {
  GamingConfiguration,
  GamingSegment,
  GAME_TYPE,
  SEGMENT_TYPE,
  PlayGameResult,
  PLAY_FREQUENCY,
} from "../../constants/types";
import { usePlayGame, useCanCustomerPlay } from "../../hooks/entity";
import { useLmcUserView } from "../../contexts/AuthContext";
import LoyaltyEmailLogin from "../molecules/LoyaltyEmailLogin";
import {
  HiOutlineExclamationTriangle,
  HiOutlineFaceFrown,
  HiOutlineCheckCircle,
} from "react-icons/hi2";
import LanguageSelector from "../atoms/LanguageSelector";
import { CustomButton } from "../atoms/CustomButton";
import GamingFooter from "./GamingFooter";
import { nextPlayLabel } from "./nextPlayLabel";
import { PiConfettiFill, PiHandTapFill } from "react-icons/pi";
import SocialAuth from "../pages/lmcUserVIew/SocialAuth";
import { IResolveParams } from "reactjs-social-login";
import { useUserSocialLogin } from "../../hooks/users";
import { toast } from "react-toastify";
import useAuth from "../../hooks/useAuth";
import { PlanTier, usePermissions } from "../../utils/permissions";
import { BsHourglassSplit } from "react-icons/bs";
import LmcUserViewForgotPassword from "../pages/lmcUserVIew/LmcUserViewForgotPassword";
import { HiOutlineArrowLeft } from "react-icons/hi";

interface GameOverlayProps {
  entityIdentification: string;
  gamingConfig: GamingConfiguration | null;
  onClose: () => void;
  onLoginRequired: () => void;
}

type GameState =
  | "loading"
  | "can_play"
  | "cannot_play"
  | "playing"
  | "result"
  | "error";

const GameOverlay: React.FC<GameOverlayProps> = ({
  entityIdentification,
  gamingConfig,
  onClose,
  onLoginRequired,
}) => {
  const { t } = useTranslation("clientView");
  const {
    state: { auth },
  } = useLmcUserView();

  const { user } = useAuth();
  const permissions = usePermissions(user);
  const showFooter = !permissions.isAtLeastTier(PlanTier.LARGE_BUSINESS);

  const [gameState, setGameState] = useState<GameState>("loading");
  const [gameResult, setGameResult] = useState<PlayGameResult | null>(null);
  const [winningSegmentId, setWinningSegmentId] = useState<string | undefined>(
    undefined
  );
  const [nextPlayAt, setNextPlayAt] = useState<string | null>(null);
  const [spinnerSize, setSpinnerSize] = useState(300);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  useEffect(() => {
    const calculateSpinnerSize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 360) {
        setSpinnerSize(220);
      } else if (screenWidth < 400) {
        setSpinnerSize(250);
      } else if (screenWidth < 480) {
        setSpinnerSize(270);
      } else {
        setSpinnerSize(300);
      }
    };

    calculateSpinnerSize();
    window.addEventListener("resize", calculateSpinnerSize);
    return () => window.removeEventListener("resize", calculateSpinnerSize);
  }, []);

  const userIdentification = auth?.user_identification;
  const isAuthenticated = auth?.isAuthenticated;

  const authToken = useMemo(() => {
    return {
      username: auth?.user_identification || "",
      password: auth?.access_token || "",
    };
  }, [auth?.user_identification, auth?.access_token]);

  const { data: canPlayData, isLoading: isCheckingCanPlay } =
    useCanCustomerPlay(
      entityIdentification,
      userIdentification || "",
      authToken,
      {
        enabled:
          !!userIdentification &&
          !!gamingConfig?.enabled &&
          !!auth?.access_token &&
          !auth.skip_gaming,
      }
    );

  const playGameMutation = usePlayGame();
  const { mutate: socialLogin } = useUserSocialLogin();

  useEffect(() => {
    if (!gamingConfig?.enabled || auth.skip_gaming) {
      onClose();
      return;
    }

    if (!isAuthenticated) {
      setGameState("can_play");
      return;
    }

    if (isCheckingCanPlay) {
      setGameState("loading");
      return;
    }

    if (canPlayData) {
      if (canPlayData.canPlay) {
        setGameState("can_play");
      } else {
        setGameState("cannot_play");
        setNextPlayAt(canPlayData.nextPlayAt || null);
      }
    } else {
      setGameState("error");
    }
  }, [gamingConfig, isAuthenticated, isCheckingCanPlay, canPlayData, onClose]);

  const handlePlay = useCallback(async () => {
    if (!userIdentification) {
      toast.error(t("games.overlay.errorMessage"));
      return;
    }
    if (auth.skip_gaming) {
      return;
    }

    setGameState("playing");

    try {
      const response = await playGameMutation.mutateAsync({
        entity_identification: entityIdentification,
        user_identification: userIdentification,
        authToken: authToken,
      });
      const result = response?.data?.element || response?.data;

      setGameResult(result);

      if (result.result && result.segment) {
        setWinningSegmentId(result.segment.id);
      } else {
        setGameState("error");
      }
    } catch (error: any) {
      const errorData = error?.response?.data;
      if (
        errorData?.element?.canPlay === false &&
        errorData?.element?.nextPlayAt
      ) {
        setNextPlayAt(errorData.element.nextPlayAt);
        setGameState("cannot_play");
      } else {
        setGameState("error");
      }
    }
  }, [playGameMutation, entityIdentification, userIdentification, t]);

  const handleLoginSuccess = useCallback(() => {}, []);

  useEffect(() => {
    const isScratchCard = gamingConfig?.game_type !== GAME_TYPE.SPINNER;
    if (
      isAuthenticated &&
      showLoginForm &&
      isScratchCard &&
      canPlayData?.canPlay
    ) {
      setShowLoginForm(false);
      handlePlay();
    }
  }, [
    isAuthenticated,
    showLoginForm,
    gamingConfig?.game_type,
    canPlayData?.canPlay,
  ]);

  const handleResolveSocialAuth = useCallback(
    (provider: IResolveParams["provider"], data: IResolveParams["data"]) => {
      const cpPayload = {
        access_token:
          provider === "google"
            ? data?.access_token
            : data?.authorization?.id_token,
        provider,
      };
      socialLogin({
        entity_identification: entityIdentification,
        data: { type: "login", providerPayload: cpPayload },
      });
    },
    [entityIdentification, socialLogin]
  );

  const handleRejectSocialAuth = useCallback(
    (err: any) => {
      console.log(err);
    },
    [t]
  );

  const handleSpinComplete = useCallback((segment: GamingSegment) => {
    setTimeout(() => {
      setGameState("result");
    }, 500);
  }, []);

  const handleScratchComplete = useCallback((segment: GamingSegment) => {
    setGameState("result");
  }, []);

  if (!gamingConfig?.enabled || auth.skip_gaming) {
    return null;
  }

  const segments = gamingConfig.segments || [];
  const isSpinner = gamingConfig.game_type === GAME_TYPE.SPINNER;
  const isPrize =
    gameResult?.result === "WIN" &&
    gameResult?.segment?.type === SEGMENT_TYPE.PRIZE;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm overflow-y-auto py-4"
      translate="no"
    >
      <div className="relative w-full max-w-md mx-4 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl shadow-lg overflow-hidden my-auto">
        <div className="p-6 text-center border-b border-gray-700">
          <h1 className="text-xl font-bold text-white mb-1">
            {t("games.overlay.title")}
          </h1>
          <p className="text-gray-400 text-sm">
            {gamingConfig?.description || ""}
          </p>
        </div>

        <div className="p-6 flex flex-col items-center">
          {gameState === "loading" && (
            <div className="py-12 flex flex-col items-center">
              <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-gray-400">{t("games.overlay.loading")}</p>
            </div>
          )}

          {gameState === "cannot_play" && (
            <div className="py-12 flex flex-col items-center text-center">
              {gamingConfig?.play_frequency === PLAY_FREQUENCY.ONCE ? (
                <HiOutlineCheckCircle className="w-16 h-16 text-green-400 mb-4" />
              ) : (
                <BsHourglassSplit className="w-16 h-16 text-purple-400 mb-4" />
              )}
              <h3 className="text-xl font-bold text-white mb-2">
                {t("games.overlay.playedRecently")}
              </h3>
              {gamingConfig?.play_frequency === PLAY_FREQUENCY.ONCE ? (
                <p className="text-gray-400 mb-4">
                  {t("games.overlay.oneTimeOnly")}
                </p>
              ) : (
                <p className="text-gray-400 mb-4">
                  {t("games.overlay.tryAgainIn")}{" "}
                  <span className="text-purple-400 font-bold">
                    {nextPlayAt ? nextPlayLabel(t, nextPlayAt) : ""}
                  </span>
                </p>
              )}
              <CustomButton
                variant="gray"
                size="lg"
                onClick={onClose}
                title={t("games.overlay.continue")}
              />
            </div>
          )}

          {gameState === "can_play" && !isAuthenticated && !showLoginForm && (
            <>
              {isSpinner ? (
                <div
                  className="cursor-pointer"
                  onClick={() => setShowLoginForm(true)}
                >
                  <SpinnerWheel
                    segments={segments}
                    onSpinComplete={handleSpinComplete}
                    winningSegmentId={undefined}
                    disabled={true}
                    isLoading={false}
                    size={spinnerSize}
                  />
                </div>
              ) : (
                <div
                  className="cursor-pointer"
                  onClick={() => setShowLoginForm(true)}
                >
                  <div
                    className="rounded-xl overflow-hidden hover:scale-105 transition-transform shadow-lg"
                    style={{
                      width: 260,
                      height: 173,
                      background:
                        "linear-gradient(145deg, #d4af37 0%, #94771d 25%, #e6c547 50%, #b8960f 75%, #d4af37 100%)",
                      boxShadow:
                        "inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.3)",
                    }}
                  >
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      <PiHandTapFill className="w-12 h-12 text-amber-900 mb-2 drop-shadow-lg" />
                      <p className="text-lg font-bold text-amber-900 drop-shadow-sm">
                        {t("games.scratch.scratchHere")}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-col gap-3 w-full max-w-xs">
                <button
                  onClick={onClose}
                  className="w-full px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm"
                >
                  {t("games.overlay.skip")}
                </button>
              </div>
            </>
          )}

          {gameState === "can_play" &&
            !isAuthenticated &&
            showLoginForm &&
            !showForgotPassword && (
              <div className="w-full flex flex-col gap-3">
                <div className="flex items-center justify-center mb-2">
                  <span className="text-white font-medium">{t("key25")}</span>
                </div>

                <div className="w-full flex-col justify-between items-center flex gap-2">
                  <SocialAuth
                    className="w-full"
                    providers={["Google", "Apple"]}
                    onLoginStartSocialAuthStart={() => {}}
                    onResolveSocialAuth={handleResolveSocialAuth}
                    onRejectSocialAuth={handleRejectSocialAuth}
                    redirectUrl={{
                      Apple: `${process.env.REACT_APP_APPLE_CALLBACK}`,
                    }}
                    providersIcon={{
                      Apple: (
                        <div className="bg-gray-700 hover:bg-gray-600 rounded-xl px-4 w-full h-11 flex flex-row justify-center items-center cursor-pointer relative border border-gray-600 transition-all duration-200 shadow-sm">
                          <img
                            src="../../images/apple.svg"
                            alt=""
                            className="w-5 h-5 absolute left-4"
                          />
                          <p className="text-white text-sm font-semibold font-figtree leading-normal">
                            {t("key18")}
                          </p>
                        </div>
                      ),
                      Google: (
                        <div className="bg-gray-700 hover:bg-gray-600 rounded-xl px-4 w-full h-11 flex flex-row justify-center items-center cursor-pointer relative border border-gray-600 transition-all duration-200 shadow-sm">
                          <img
                            src="../../images/google.svg"
                            alt=""
                            className="w-5 h-5 absolute left-4"
                          />
                          <p className="text-white text-sm font-semibold font-figtree leading-normal">
                            {t("key19")}
                          </p>
                        </div>
                      ),
                    }}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-white/20"></div>
                  <p className="text-center text-gray-400 text-xs font-medium font-figtree uppercase leading-none">
                    {t("key20")}
                  </p>
                  <div className="flex-1 h-px bg-white/20"></div>
                </div>

                <LoyaltyEmailLogin
                  onLoginSuccess={handleLoginSuccess}
                  onForgotPassword={() => setShowForgotPassword(true)}
                />

                <button
                  onClick={onClose}
                  className="w-full px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm mt-2"
                >
                  {t("games.overlay.skip")}
                </button>
              </div>
            )}

          {gameState === "can_play" &&
            !isAuthenticated &&
            showLoginForm &&
            showForgotPassword && (
              <div className="w-full flex flex-col gap-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {t("key57")}
                    </h3>
                    <p className="text-sm text-white/70 leading-relaxed mt-1">
                      {t("key66")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="p-2 rounded-full bg-white/10 border border-white/15 text-white hover:bg-white/20 transition"
                  >
                    <HiOutlineArrowLeft className="w-5 h-5" />
                  </button>
                </div>

                <LmcUserViewForgotPassword />

                <button
                  onClick={onClose}
                  className="w-full px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm mt-2"
                >
                  {t("games.overlay.skip")}
                </button>
              </div>
            )}

          {(gameState === "can_play" || gameState === "playing") &&
            isAuthenticated && (
              <>
                {isSpinner ? (
                  <SpinnerWheel
                    segments={segments}
                    onSpinComplete={handleSpinComplete}
                    winningSegmentId={winningSegmentId}
                    disabled={gameState === "can_play"}
                    isLoading={gameState === "playing"}
                    size={spinnerSize}
                  />
                ) : gameState === "playing" && gameResult?.segment ? (
                  <ScratchCard
                    segment={
                      segments.find((s) => s.id === gameResult.segment?.id) ||
                      segments[0]
                    }
                    onScratchComplete={handleScratchComplete}
                    width={280}
                    height={180}
                  />
                ) : (
                  <div className="cursor-pointer" onClick={handlePlay}>
                    <div
                      className="rounded-xl overflow-hidden hover:scale-105 transition-transform shadow-lg"
                      style={{
                        width: 260,
                        height: 173,
                        background:
                          "linear-gradient(145deg, #d4af37 0%, #94771d 25%, #e6c547 50%, #b8960f 75%, #d4af37 100%)",
                        boxShadow:
                          "inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.3)",
                      }}
                    >
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        <PiHandTapFill className="w-12 h-12 text-amber-900 mb-2 drop-shadow-lg" />
                        <p className="text-lg font-bold text-amber-900 drop-shadow-sm">
                          {t("games.scratch.scratchHere")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {gameState === "can_play" && (
                  <div className="mt-6 flex flex-col gap-3 w-full max-w-xs">
                    {isSpinner && (
                      <button
                        onClick={handlePlay}
                        className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-purple-600 hover:from-purple-700 hover:to-purple-700 text-white rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-lg"
                      >
                        {t("games.spinner.spin")}
                      </button>
                    )}

                    <button
                      onClick={onClose}
                      className="w-full px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm"
                    >
                      {t("games.overlay.skip")}
                    </button>
                  </div>
                )}
              </>
            )}

          {gameState === "playing" && !isSpinner && !gameResult?.segment && (
            <div className="py-12 flex flex-col items-center">
              <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-gray-400">{t("games.overlay.processing")}</p>
            </div>
          )}

          {gameState === "result" && gameResult && (
            <div className="py-8 flex flex-col items-center text-center">
              {isPrize ? (
                <>
                  <PiConfettiFill className="w-20 h-20 text-amber-400 mb-4 animate-bounce" />
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {t("games.overlay.congratulations")}
                  </h3>
                  {segments.find((s) => s.id === gameResult.segment?.id)
                    ?.image_url && (
                    <div className="mb-3">
                      <img
                        src={
                          segments.find((s) => s.id === gameResult.segment?.id)
                            ?.image_url
                        }
                        alt={gameResult.segment?.display_text}
                        className="w-24 h-24 rounded-md object-cover border-2 border-white shadow-lg"
                      />
                    </div>
                  )}
                  <p className="text-lg text-purple-400 font-medium mb-2">
                    {gameResult.segment?.display_text}
                  </p>
                  {gameResult.prize?.description && (
                    <p className="text-gray-400 text-sm mb-4">
                      {gameResult.prize.description}
                    </p>
                  )}
                  {gameResult.prize_applied && (
                    <div className="bg-green-500/20 text-green-400 px-4 py-2 rounded-lg text-sm mb-4">
                      {t("games.overlay.prizeApplied")}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <HiOutlineFaceFrown className="w-20 h-20 text-gray-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">
                    {t("games.overlay.betterLuck")}
                  </h3>
                  {segments.find((s) => s.id === gameResult.segment?.id)
                    ?.image_url && (
                    <div className="mb-3">
                      <img
                        src={
                          segments.find((s) => s.id === gameResult.segment?.id)
                            ?.image_url
                        }
                        alt={gameResult.segment?.display_text}
                        className="w-24 h-24 rounded-md object-cover border-2 border-white shadow-lg"
                      />
                    </div>
                  )}
                  <p className="text-gray-400 mb-4">
                    {gameResult.segment?.display_text}
                  </p>
                </>
              )}

              <button
                onClick={onClose}
                className="mt-4 px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-600 hover:from-purple-700 hover:to-purple-700 text-white rounded-full font-bold transition-all transform hover:scale-105"
              >
                {t("games.overlay.viewCard")}
              </button>
            </div>
          )}

          {gameState === "error" && (
            <div className="py-12 flex flex-col items-center text-center">
              <HiOutlineExclamationTriangle className="w-16 h-16 text-amber-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">
                {t("games.overlay.error")}
              </h3>
              <p className="text-gray-400 mb-4">
                {gameResult?.error || t("games.overlay.errorMessage")}
              </p>
              <div className="flex gap-3">
                <CustomButton
                  variant="primary"
                  size="lg"
                  onClick={handlePlay}
                  title={t("games.overlay.tryAgainBtn")}
                />
                <CustomButton
                  variant="gray"
                  size="lg"
                  onClick={onClose}
                  title={t("games.overlay.skip")}
                />
              </div>
            </div>
          )}
        </div>

        <GamingFooter showFooter={showFooter} variant="dark" />

        <div className="absolute top-4 right-4">
          <LanguageSelector variant="compact" />
        </div>
      </div>
    </div>
  );
};

export default GameOverlay;
